import React from 'react';
import PropTypes from 'prop-types';

import Image from '$partials/media/image';
import Button from '$partials/ui/button.jsx';
import {
    IconChevronSprite
} from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleTextAndImage = ({
    eyebrowTitle,
    eyebrowIcon,
    title,
    textAndImageBody,
    cta1Link,
    cta1Label,
    cta2Link,
    cta2Label,
    image,
    imageTiled,
    parent,
}) => {
    const { formatURI } = useLocale();

    if (!title) { return null; }

    return (
        <div className={`${(parent === 'Home' || parent === 'Listing') ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'} bg-white relative`}>
            <div className='layout-container'>

                <div className='bg-white relative w-full flex flex-col sm:flex-row gap-y-20'>

                    {/* Text content */}
                    <div className={`px-10 sm:px-5 md:px-10 h-min my-auto w-full ${image ? 'w-full sm:w-3/6 md:w-5/12' : 'sm:w-9/12'}`}>
                        <div className='flex flex-row items-center mb-16'>
                            {eyebrowIcon && (
                                <div
                                    className='mr-10 pointer-events-none select-none'
                                    style={{
                                        fontSize: '18px',
                                        width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em`,
                                        maxWidth: '100%'
                                    }}>
                                    <Image
                                        {...eyebrowIcon}
                                        loading={''} />
                                </div>
                            )}
                            {eyebrowTitle && (
                                <p className='type-style-7 text-orange'>
                                    {eyebrowTitle}
                                </p>
                            )}
                        </div>
                        <p className='type-style-2 mb-12'>
                            {title}
                        </p>
                        <p className='type-style-5 text-dark-grey'>
                            {textAndImageBody}
                        </p>
                        {((cta1Label && cta1Link) || (cta2Label && cta2Link)) && (
                            <div className='flex flex-row flex-wrap items-center gap-10 mt-20'>
                                {cta1Label && cta1Link && (
                                    <div className='flex self-start'>
                                        <Button
                                            as='a'
                                            className={`button ${cta2Label && cta2Link ? 'button--primary' : 'button--secondary-alt'}`}
                                            href={formatURI(cta1Link)}>
                                            <span>{cta1Label}</span>
                                            {!cta2Label && !cta2Link && (
                                                <span className='button__icon button__icon--animate'>
                                                    <IconChevronSprite />
                                                </span>
                                            )}
                                        </Button>
                                    </div>
                                )}
                                {cta2Label && cta2Link && (
                                    <div className='flex'>
                                        <Button
                                            as='a'
                                            className='button button--secondary'
                                            href={formatURI(cta2Link)}>
                                            <span>{cta2Label}</span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>

                    {image && !imageTiled && (
                        <>
                            {/* spacer */}
                            <div className='px-10 hidden md:block w-1/12' />

                            {/* image */}
                            <div className='px-10 sm:px-5 md:px-10 w-full sm:w-6/12'>
                                <figure className='rounded-button overflow-hidden h-[447px] pointer-events-none select-none'>
                                    <Image
                                        className='h-full object-cover'
                                        containerClassName='h-full object-cover'
                                        height={'100%'}
                                        loadWidth={840}
                                        src={image?.src}
                                        width={'100%'} />
                                </figure>
                            </div>
                        </>
                    )}

                    {image && imageTiled && (
                        <>
                            {/* spacer */}
                            <div className='px-10 hidden md:block w-1/12' />

                            {/* image repeated */}
                            <div className='px-10 sm:px-5 md:px-10 w-full sm:w-6/12'>
                                <figure className='rounded-button overflow-hidden relative min-h-[447px]'>
                                    <div
                                        className='absolute top-0 left-0 h-full w-full md:w-[55%] bg-no-repeat bg-left-top bg-cover'
                                        style={{
                                            backgroundImage: `url(${image.src}?fm=webp&q=50&w=1120)`
                                        }} />
                                    <div
                                        className='md:absolute top-0 left-[55%] h-full w-[45%] bg-no-repeat bg-left-top bg-cover'
                                        style={{
                                            backgroundImage: `url(${image.src}?fm=webp&q=50&w=1120)`
                                        }} />
                                </figure>
                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModuleTextAndImage.propTypes = {
    eyebrowTitle: PropTypes.string,
    eyebrowIcon: PropTypes.object,
    title: PropTypes.string,
    textAndImageBody: PropTypes.string,
    cta1Link: PropTypes.string,
    cta1Label: PropTypes.string,
    cta2Link: PropTypes.string,
    cta2Label: PropTypes.string,
    image: PropTypes.object,
    imageTiled: PropTypes.bool,
    parent: PropTypes.string,
};

export default ModuleTextAndImage;
